import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { HStack, VStack } from "../../components/utils";
import {
  AllocationMode,
  CostOrGain,
  QuantumMode,
  RuleScope,
} from "../../store/useAllocationMatrixStore";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import {
  AllocationEvaluation,
  useEmployeeGrants,
} from "../../queries/allocationMatrix";
import { useValuationDetails } from "../../queries/Valuation";
import { ValuationSource } from "../../types/ValuationType";
import { useCompanyStore } from "../../store";
import { calculateAllocationValue } from "../../utils/allocationMatrixValue";

const FormulaCardTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
    color: "#4b5563",
    maxWidth: "none",
    padding: 0,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#fff",
    "&::before": {
      border: "1px solid #e5e7eb",
    },
  },
});

function AllocatedTooltip({
  companyType,
  currentSharePrice,
}: {
  companyType: string;
  currentSharePrice: number;
}) {
  const isPublic = companyType === "PUBLIC";
  return (
    <div className="relative group inline-flex items-center">
      <Icon
        icon="ri:information-line"
        height={14}
        className="text-gray-400 cursor-pointer hover:text-gray-600"
      />
      <div className="absolute top-full left-0 mt-2 hidden group-hover:block z-50 pointer-events-none">
        <div className="w-2.5 h-2.5 bg-white border-t border-l border-gray-200 rotate-45 ml-2 -mb-[5px]" />
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 whitespace-nowrap">
          <p className="text-xs text-gray-600">
            Calculated using the{" "}
            {isPublic ? "latest share price" : "latest valuation"} as of today (
            {formatCurrency(currentSharePrice, getCurrencyType())}).
          </p>
        </div>
      </div>
    </div>
  );
}

type TabId = "eligibility" | "rules" | "utilization";

interface AllocationRule {
  allocationMode?: AllocationMode;
  costOrGain?: CostOrGain;
  quantumMin?: number | null;
  quantumRecommended?: number | null;
  quantumMax?: number | null;
  scope?: RuleScope;
  quantumMode?: QuantumMode;
}

interface AllocationMatrixInfoCardProps {
  rule: AllocationRule | null;
  grantValue: number;
  isAboveLimit: boolean;
  isBelowLimit: boolean;
  employeeId?: string;
  evalData?: AllocationEvaluation | null;
  sharePrice?: number;
  currentSharePrice?: number;
  grantPrice?: number;
  currentGrantId?: string;
  employeeGrants?: any[];
  managerScoped?: boolean;
}

export function FormulaTooltip({ costOrGain }: { costOrGain: CostOrGain }) {
  const isGain = costOrGain === CostOrGain.GAIN;
  return (
    <FormulaCardTooltip
      arrow
      placement="bottom-start"
      title={
        <div className="w-[250px] px-4 py-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            {isGain ? "Gain Formula" : "Cost Formula"}
          </p>
          <div className="flex items-baseline gap-1 text-sm font-medium text-gray-800">
            <span className="italic">V</span>
            <span className="text-gray-400 mx-0.5">=</span>
            <span className="italic">n</span>
            <span className="text-gray-400 mx-0.5">×</span>
            {isGain ? (
              <>
                <span className="text-gray-500">(</span>
                <span className="italic text-blue-600">FMV</span>
                <span className="text-gray-400 mx-0.5">−</span>
                <span className="italic text-orange-501">P</span>
                <sub className="text-orange-501 text-xs -ml-0.5">strike</sub>
                <span className="text-gray-500">)</span>
              </>
            ) : (
              <span className="italic text-blue-600">FMV</span>
            )}
          </div>
          <div className="mt-2.5 pt-2 border-t border-gray-100 flex flex-col gap-0.5 text-xs text-gray-500">
            <span>
              <span className="italic font-semibold text-gray-700">V</span> —
              monetary value
            </span>
            <span>
              <span className="italic font-semibold text-gray-700">n</span> —
              number of options
            </span>
            <span>
              <span className="italic font-semibold text-blue-600">FMV</span> —
              fair market value (share price)
            </span>
            {isGain && (
              <span>
                <span className="italic font-semibold text-orange-501">P</span>
                <sub className="text-xs text-orange-501">strike</sub> — grant /
                strike price
              </span>
            )}
          </div>
        </div>
      }
    >
      <span className="inline-flex cursor-help">
        <Icon
          icon="ri:information-line"
          height={14}
          className="text-gray-400 hover:text-gray-600"
        />
      </span>
    </FormulaCardTooltip>
  );
}

const TABS: { id: TabId; label: string }[] = [
  { id: "eligibility", label: "Eligibility" },
  { id: "rules", label: "Allocation Rules" },
  { id: "utilization", label: "Utilization History" },
];

export function AllocationMatrixInfoCard({
  rule,
  grantValue,
  isAboveLimit,
  isBelowLimit,
  employeeId,
  evalData,
  sharePrice = 0,
  currentSharePrice: currentSharePriceOverride,
  grantPrice = 0,
  currentGrantId,
  employeeGrants: employeeGrantsOverride,
  managerScoped = false,
}: AllocationMatrixInfoCardProps) {
  const currencyType = getCurrencyType();
  const [activeTab, setActiveTab] = useState<TabId>("rules");

  // Determine current share price for utilization calculation (same as AllocationEnvelopePanel)
  const { data: _valuationData, isPlaceholderData } =
    useValuationDetails(managerScoped);
  const { companyData } = useCompanyStore();

  const currentSharePrice = useMemo(() => {
    if (currentSharePriceOverride !== undefined) {
      return currentSharePriceOverride;
    }

    const isPublic = companyData?.companyMetaData.typeOfTheCompany === "PUBLIC";
    if (isPublic) {
      const publicPps = companyData?.companyMetaData?.pps;
      if (publicPps) return publicPps;
    } else if (
      _valuationData &&
      !isPlaceholderData &&
      _valuationData.length > 0
    ) {
      let valuation = _valuationData.find(
        (v: any) => v.valuationSource === ValuationSource.COMPANY
      );
      if (!valuation) {
        valuation = _valuationData[0];
      }
      if (valuation?.pps) return valuation.pps;
    }
    return sharePrice || 0;
  }, [
    currentSharePriceOverride,
    _valuationData,
    isPlaceholderData,
    companyData,
    sharePrice,
  ]);

  // Fetch employee grants for utilization history
  const { data: employeeGrantsData, isFetching: grantsFetching } =
    useEmployeeGrants(employeeId ?? undefined, !employeeGrantsOverride);

  if (!rule) return null;

  const isValueMode = rule.allocationMode === AllocationMode.ABSOLUTE_VALUE;
  const costOrGain = rule.costOrGain ?? null;
  const isPerEmployee = rule.scope === RuleScope.PER_EMPLOYEE;

  // Get matched factors, excluding "any" values
  const eligibilityChips = useMemo((): { label: string; value: string }[] => {
    if (evalData?.matchedOn && Object.keys(evalData.matchedOn).length > 0) {
      return Object.entries(evalData.matchedOn).flatMap(([field, val]) => {
        const values = Array.isArray(val) ? val : [val];
        const label = field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (s) => s.toUpperCase());
        return values
          .filter((v) => v?.toLowerCase() !== "any")
          .map((v) => ({ label, value: v }));
      });
    }
    if (evalData?.matchedConditions && evalData.matchedConditions.length > 0) {
      return evalData.matchedConditions
        .filter((c) => c.value?.toLowerCase() !== "any")
        .map((c) => ({
          label: c.label,
          value: c.value,
        }));
    }

    return [];
  }, [evalData?.matchedOn, evalData?.matchedConditions]);

  // Calculate utilization history
  const employeeGrants = useMemo(
    () =>
      (employeeGrantsOverride ?? employeeGrantsData ?? []).filter(
        (g) =>
          g &&
          (g.grantIdentifier || g.id) &&
          g.optionHolderState !== "WITHDRAW" &&
          !(
            currentGrantId &&
            (g.grantIdentifier === currentGrantId || g.id === currentGrantId)
          )
      ),
    [employeeGrantsData, employeeGrantsOverride, currentGrantId]
  );

  const allocatedOptionCount = useMemo(
    () => employeeGrants.reduce((sum, g) => sum + (g.optionsGranted || 0), 0),
    [employeeGrants]
  );

  const allocated = useMemo(
    () =>
      employeeGrants.reduce(
        (sum, g) =>
          sum +
          calculateAllocationValue({
            options: g.optionsGranted || 0,
            ratio: g.ratio,
            fmv: currentSharePrice,
            strikePrice: g.grantPrice || 0,
            allocationMode: rule.allocationMode,
            costOrGain,
          }),
        0
      ),
    [employeeGrants, currentSharePrice, rule.allocationMode, costOrGain]
  );

  const remaining = (v: number | null) =>
    v === null ? null : isPerEmployee ? Math.max(v - allocated, 0) : v;

  const origFloor = rule.quantumMin ?? null;
  const origTarget = rule.quantumRecommended ?? null;
  const origCeiling = rule.quantumMax ?? null;
  const floor = remaining(origFloor);
  const ceiling = remaining(origCeiling);
  const isAdjusted = isPerEmployee && allocated > 0;

  // Fall back to the midpoint when the remaining recommendation is exhausted,
  // matching the allocation envelope shown during grant creation.
  const remainingTarget = remaining(origTarget);
  const target =
    isAdjusted &&
    floor !== null &&
    ceiling !== null &&
    (remainingTarget === null || remainingTarget <= 0)
      ? Math.round((floor + ceiling) / 2)
      : remainingTarget;

  const cap = evalData?.utilization?.totalPool ?? origCeiling ?? null;
  const available = useMemo(
    () =>
      evalData?.utilization?.available ??
      (cap !== null ? Math.max(cap - allocated, 0) : null),
    [cap, allocated, evalData?.utilization?.available]
  );

  const allocatedPct = useMemo(() => {
    if (!cap || cap <= 0) return 0;
    return Math.max(0, Math.min((allocated / cap) * 100, 100));
  }, [cap, allocated]);

  function fmtQuantum(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value))
      return "—";
    if (isValueMode) return formatCurrency(value, currencyType);
    return `${value.toLocaleString(currencyType)} Options`;
  }

  type EnvelopeStatus = "empty" | "below" | "within" | "above";
  let envelopeStatus: EnvelopeStatus = "within";
  if (!grantValue) envelopeStatus = "empty";
  else if (isBelowLimit) envelopeStatus = "below";
  else if (isAboveLimit) envelopeStatus = "above";

  const statusBadge: Record<
    EnvelopeStatus,
    { label: string; className: string } | null
  > = {
    empty: null,
    below: { label: "Below Minimum", className: "bg-amber-100 text-amber-700" },
    within: { label: "Within Range", className: "bg-blue-100 text-blue-700" },
    above: { label: "Exceeds Maximum", className: "bg-rose-100 text-rose-600" },
  };

  const bounds = [
    rule.quantumMin != null &&
      rule.quantumMode !== QuantumMode.MAX && {
        key: "floor",
        label: "Minimum",
        value: floor as number,
        original: origFloor,
        activeOn: "below" as EnvelopeStatus,
        activeClass: "border-amber-300 bg-amber-50",
      },
    target != null && {
      key: "target",
      label: "Recommended",
      value: target,
      original: origTarget,
      activeOn: "within" as EnvelopeStatus,
      activeClass: "border-orange-300 bg-orange-50",
    },
    ceiling != null &&
      rule.quantumMode !== QuantumMode.MIN && {
        key: "ceiling",
        label: "Maximum",
        value: ceiling as number,
        original: origCeiling,
        activeOn: "above" as EnvelopeStatus,
        activeClass: "border-rose-300 bg-rose-50",
      },
  ].filter(Boolean) as {
    key: string;
    label: string;
    value: number;
    original: number | null;
    activeOn: EnvelopeStatus;
    activeClass: string;
  }[];

  return (
    <div className="border border-borderColor rounded-lg shadow-box bg-white">
      <HStack className="border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-xs font-semibold uppercase tracking-wide border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-orange-501 text-orange-501"
                : "border-transparent text-gray-401 hover:text-black-501"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </HStack>

      <VStack className="p-4 gap-4">
        {/* Eligibility Tab */}
        {activeTab === "eligibility" && (
          <VStack className="gap-4">
            <HStack className="items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600">
                <Icon icon="ri:checkbox-circle-fill" height={20} />
              </div>
              <span className="text-sm font-semibold text-primaryText">
                Eligible for allocation
              </span>
            </HStack>
            {eligibilityChips.length > 0 && (
              <VStack className="gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-light">
                  Matched Factors
                </p>
                <HStack className="flex-wrap gap-2">
                  {eligibilityChips.map((chip) => (
                    <span
                      key={`${chip.label}:${chip.value}`}
                      className="px-3 py-1 rounded-md bg-gray-100 text-sm text-gray-600"
                    >
                      <span className="font-medium text-gray-500">
                        {chip.label}:
                      </span>{" "}
                      {chip.value}
                    </span>
                  ))}
                </HStack>
              </VStack>
            )}
          </VStack>
        )}

        {/* Allocation Rules Tab */}
        {activeTab === "rules" && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <HStack className="items-center gap-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-light">
                  Allocation Envelope
                </p>
                {isValueMode && costOrGain && (
                  <FormulaTooltip costOrGain={costOrGain} />
                )}
              </HStack>
              {statusBadge[envelopeStatus] && (
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    statusBadge[envelopeStatus]!.className
                  }`}
                >
                  {statusBadge[envelopeStatus]!.label}
                </span>
              )}
            </HStack>

            <HStack className="gap-3">
              {bounds.map((b) => {
                const isActive = envelopeStatus === b.activeOn;
                return (
                  <VStack
                    key={b.key}
                    className={`flex-1 gap-1 p-3 border rounded-lg transition-colors ${
                      isActive ? b.activeClass : "border-borderColor"
                    }`}
                  >
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-light">
                      {b.label}
                    </span>
                    <span className="text-sm font-semibold text-primaryText">
                      {fmtQuantum(b.value)}
                    </span>
                    {b.original != null && b.value !== b.original && (
                      <span className="text-xs text-gray-light">
                        of {fmtQuantum(b.original)}
                      </span>
                    )}
                  </VStack>
                );
              })}
            </HStack>

            {envelopeStatus === "within" && (
              <HStack className="items-center gap-2 p-3 rounded bg-blue-50 text-xs text-blue-700">
                <Icon
                  icon="ri:checkbox-circle-line"
                  className="flex-shrink-0"
                  height={16}
                />
                <span>
                  Grant value{" "}
                  <span className="font-semibold">
                    ({fmtQuantum(grantValue)})
                  </span>{" "}
                  is within the recommended range for this rule.
                </span>
              </HStack>
            )}
            {envelopeStatus === "below" && (
              <HStack className="items-start gap-2 p-3 rounded bg-amber-50 text-xs text-amber-700">
                <Icon
                  icon="ri:alert-line"
                  className="flex-shrink-0 mt-0.5"
                  height={16}
                />
                <span>
                  Grant value falls below the minimum. This grant will be
                  flagged for review during approval.
                </span>
              </HStack>
            )}
            {envelopeStatus === "above" && (
              <HStack className="items-start gap-2 p-3 rounded bg-rose-50 text-xs text-rose-600">
                <Icon
                  icon="ri:error-warning-line"
                  className="flex-shrink-0 mt-0.5"
                  height={16}
                />
                <span>
                  Grant value exceeds the maximum. This grant will be flagged
                  during approval and review.
                </span>
              </HStack>
            )}
          </VStack>
        )}

        {/* Utilization / Summary Tab */}
        {activeTab === "utilization" && (
          <VStack className="gap-4">
            {!isPerEmployee ? (
              <HStack className="items-center gap-2 p-3 rounded bg-gray-50 text-sm text-gray-light">
                <Icon
                  icon="ri:info-line"
                  className="flex-shrink-0"
                  height={16}
                />
                This is a per-grant rule. Allocation limits apply to each grant
                individually.
              </HStack>
            ) : !employeeId ? (
              <HStack className="items-center gap-2 p-3 rounded bg-gray-50 text-sm text-gray-light">
                <Icon
                  icon="ri:info-line"
                  className="flex-shrink-0"
                  height={16}
                />
                Employee information unavailable.
              </HStack>
            ) : grantsFetching && !employeeGrantsData ? (
              <HStack className="items-center gap-2 text-sm text-gray-light">
                <Icon
                  icon="ri:loader-4-line"
                  className="animate-spin"
                  height={16}
                />
                Loading grant history…
              </HStack>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-light">
                  Lifetime Cap Utilization
                </p>

                {cap !== null ? (
                  <>
                    <HStack className="justify-between">
                      <VStack className="gap-0.5">
                        <span className="text-xs font-medium text-gray-light uppercase">
                          Lifetime Cap
                        </span>
                        <span className="text-lg font-semibold text-primaryText">
                          {fmtQuantum(cap)}
                        </span>
                      </VStack>
                      <VStack className="gap-0.5">
                        <HStack className="items-center gap-1">
                          <span className="text-xs font-medium text-gray-light uppercase">
                            Allocated
                          </span>
                          <AllocatedTooltip
                            companyType={
                              companyData?.companyMetaData.typeOfTheCompany ??
                              ""
                            }
                            currentSharePrice={currentSharePrice}
                          />
                        </HStack>
                        <span className="text-lg font-semibold text-primaryText">
                          {fmtQuantum(allocated)}
                        </span>
                      </VStack>
                      <VStack className="gap-0.5 text-right">
                        <span className="text-xs font-medium text-gray-light uppercase">
                          Available
                        </span>
                        <span className="text-lg font-semibold text-green-600">
                          {fmtQuantum(available ?? 0)}
                        </span>
                      </VStack>
                    </HStack>

                    <div className="flex w-full h-2.5 rounded-full bg-blue-100 overflow-hidden">
                      <div
                        className="h-2.5 bg-orange-501"
                        style={{ width: `${allocatedPct}%` }}
                      />
                    </div>
                    <HStack className="gap-4 text-xs text-gray-light">
                      <HStack className="items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-orange-501" />
                        Allocated
                      </HStack>
                      <HStack className="items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-100" />
                        Available
                      </HStack>
                    </HStack>
                  </>
                ) : null}

                <HStack className="items-center gap-2 p-3 rounded bg-gray-50 text-xs text-gray-light">
                  <Icon
                    icon="ri:history-line"
                    className="text-gray-light flex-shrink-0"
                    height={16}
                  />
                  {employeeGrants.length === 0
                    ? "No previous grants issued to this employee."
                    : `This employee has ${
                        employeeGrants.length
                      } previous grant${
                        employeeGrants.length === 1 ? "" : "s"
                      }.`}
                </HStack>
              </>
            )}
          </VStack>
        )}
      </VStack>
    </div>
  );
}
